import { dayIn, instantOfWall, sameClock, wallIn } from './zones';

// When a task is due (TF-220): a moment, shown in the viewer's zone and entered in a chosen one
// (dueZone records which), or a day - "at any time" that day, on that date in every zone. Plain
// functions, tested in zones.test.ts.

export interface Due {
  dueAt: string | null;
  /** A day's task: YYYY-MM-DD. Its dueAt is only a stand-in (noon UTC). */
  dueDay?: string | null;
  /** The zone the time was entered in; null when not known (written before TF-220). */
  dueZone?: string | null;
}

/** What the date and time field holds: "2026-10-05T14:30" at a time, "2026-10-05" for a day, "" for none. */
export interface DueInput {
  wall: string;
  zone: string;
}

export const isDayTask = (due: Due) => Boolean(due.dueDay);

/** The day a task is on for the viewer: its day, or the day its moment falls on where they are. */
export function dueDayIn(due: Due, zone: string): string | null {
  if (due.dueDay) return due.dueDay;
  return due.dueAt ? dayIn(zone, due.dueAt) : null;
}

/** Past due: a moment gone by, or a day over where the viewer is. */
export function isPastDue(due: Due & { doneAt?: string | null }, zone: string, now = Date.now()): boolean {
  if (due.doneAt) return false;
  if (due.dueDay) return due.dueDay < dayIn(zone, now);
  return due.dueAt !== null && Date.parse(due.dueAt) < now;
}

/**
 * The form's value for a task: in the zone it was entered in, so its owner sees what they typed (the
 * viewer's time is said beside it), the viewer's zone for a new one or one without a zone.
 */
export function dueInput(due: Due | null | undefined, viewer: string): DueInput {
  if (!due?.dueAt) return { wall: '', zone: viewer };
  if (due.dueDay) return { wall: due.dueDay, zone: due.dueZone ?? viewer };
  const zone = due.dueZone ?? viewer;
  return { wall: wallIn(zone, due.dueAt), zone };
}

/** A start given as a moment (a click on the calendar, "tomorrow at nine") as the form's value. */
export const dueInputAt = (at: string | null, zone: string): DueInput => (at ? { wall: wallIn(zone, at), zone } : { wall: '', zone });

/** What the API takes for the form's value: a moment and its zone, a day, or nothing. */
export function dueFields(input: DueInput): { dueAt: string | null; dueDay: string | null; dueZone: string | null } {
  if (/^\d{4}-\d{2}-\d{2}$/.test(input.wall)) return { dueAt: null, dueDay: input.wall, dueZone: input.zone };
  const at = instantOfWall(input.wall, input.zone);
  return at ? { dueAt: at.toISOString(), dueDay: null, dueZone: input.zone } : { dueAt: null, dueDay: null, dueZone: null };
}

/** Whether the form's value says something other than the task does. */
export function dueChanged(input: DueInput, due: Due | null | undefined, viewer: string): boolean {
  const before = dueInput(due, viewer);
  return before.wall !== input.wall || (input.wall !== '' && before.zone !== input.zone);
}

/** Whether the task's time was entered somewhere that shows another clock than the viewer's: the mark (TF-220). */
export function enteredElsewhere(due: Due, viewer: string): string | null {
  if (!due.dueAt || due.dueDay || !due.dueZone) return null;
  return sameClock(due.dueZone, viewer, due.dueAt) ? null : due.dueZone;
}
